import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import CollectionsPanel from '../CollectionsPanel';
import CollectionsBrowser from '../CollectionsPanel/CollectionsBrowser';
import CollectionForm from '../CollectionForm';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { ContentCollectionResponse, ContentCollectionType, SmartQueryFieldContentType } from '../../../API';
import { useNavigate, useParams } from 'react-router-dom';
import { COLLECTION_PREFIX, CollectionsPanelTabViewOptions, useData } from '../../../data-layer';
import { AppRoutes } from '../../../Routes';
import CollectionManager from '../CollectionsPanel/CollectionManager';
import CollectionLayoutsModal from '../CollectionsPanel/CollectionsBrowser/CollectionLayoutsModal';
import { Allotment } from 'allotment';
import { useSplitPane } from '../../../hooks/General/useSplitPane';
import { collectionsTestIds } from '../../shared/TestsIds';
import { useCollectionDisplay } from '../../../hooks/Collections/useCollectionDisplay';
import { CollectionSkeleton } from '../CollectionSkeleton';

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex'
  },
  wrapper: {
    display: 'flex',
    height: '100%'
  }
}));

function Collections(): JSX.Element | null {
  const {
    collections: {
      state: {
        withCollectionsExpanded,
        withCollectionContentTypes,
        withCollectionQueryFields,
        withCollectionQueryOperators,
        withCollectionQuerySortings,
        withCollectionQueryOptions,
        withCollectionsPanelTabView,
        withSelected,
        withAllGenres,
        withSplitPaneSizes
      },
      hook: { getById, getSmartQueryParameters, setSelected, getGenres }
    },
    channels: {
      state: { withAllRecords: withAllChannels },
      hook: { getAll: getAllChannels }
    },
    media: {
      state: { withContentTypesAllowed }
    }
  } = useData();

  const { classes } = useStyles();
  const setCollectionContentTypes = useSetRecoilState(withCollectionContentTypes);
  const expanded = useRecoilValue(withCollectionsExpanded);
  const setWhichView = useSetRecoilState(withCollectionsPanelTabView);
  const setQuerySortings = useSetRecoilState(withCollectionQuerySortings);
  const setQueryOptions = useSetRecoilState(withCollectionQueryOptions);
  const setQueryOperators = useSetRecoilState(withCollectionQueryOperators);
  const setQueryFields = useSetRecoilState(withCollectionQueryFields);
  const [allGenres, setAllGenres] = useRecoilState(withAllGenres);
  const allChannels = useRecoilValue(withAllChannels);
  const setContentTypesAllowed = useSetRecoilState(withContentTypesAllowed);
  const navigate = useNavigate();
  const { id: collectionId } = useParams<{ id: string | undefined }>();
  const { sizes, onChange: onSplitPaneChange } = useSplitPane(withSplitPaneSizes);
  const selectedCollection = useRecoilValue(withSelected);
  const { reset: resetCollectionDisplay, getAppRoute } = useCollectionDisplay();
  const [isValidating, setIsValidating] = React.useState(false);

  const setSelectedCollection = (collection: ContentCollectionResponse | undefined) => {
    setSelected(collection?.entityId);
  };

  const getAllGenres = async () => {
    if (!allGenres?.length) {
      const genres = await getGenres();
      setAllGenres(genres);
    }
  };

  useEffect(() => {
    const { MOVIE, SERIES, EPISODE } = SmartQueryFieldContentType;
    const usedCollectionContentTypes = [MOVIE, SERIES, EPISODE];
    setCollectionContentTypes(usedCollectionContentTypes);
    getCollectionParams(usedCollectionContentTypes);
    setSelected(undefined);
    getAllGenres();
    // In case the cms user have visited the MetadataManager page before, we need to reset the contentTypesAllowed
    setContentTypesAllowed([]);
    if (!allChannels) {
      getAllChannels();
    }

    return () => {
      resetCollectionDisplay();
    };
  }, []);

  useEffect(() => {
    if (!collectionId) {
      setSelected(undefined);
      setIsValidating(false);
      return;
    }
    setIsValidating(true);
    const fullCollectionId = collectionId.startsWith(COLLECTION_PREFIX)
      ? collectionId
      : `${COLLECTION_PREFIX}${collectionId}`;
    fetchAndSetCollection(fullCollectionId);
  }, [collectionId]);

  useEffect(() => {
    if (!expanded) {
      setWhichView(CollectionsPanelTabViewOptions.VOD_SEARCH);
    }
  }, [expanded]);

  const fetchAndSetCollection = async (collectionId: string) => {
    try {
      const foundCollection = await getById(collectionId, true);
      if (foundCollection) {
        // Validate that the collection type matches the route (MIXED_LIST)
        if (foundCollection.collectionType !== ContentCollectionType.MIXED_LIST) {
          navigate(AppRoutes[404], { replace: true });
          return;
        }
        setSelected(foundCollection.entityId);
        setIsValidating(false);
        return;
      }
      navigate(getAppRoute(), { replace: true });
    } catch (err) {
      navigate(AppRoutes[404], { replace: true });
    }
  };

  const getCollectionParams = async (contentTypes: SmartQueryFieldContentType[]) => {
    const data = await getSmartQueryParameters(contentTypes);
    if (data) {
      setQuerySortings(data.sortings);
      setQueryOperators(data.operators);
      setQueryFields(data.fields);
      setQueryOptions(data.options);
    }
  };

  const handleGoBack = () => {
    navigate(getAppRoute());
    setSelectedCollection(undefined);
  };

  const getCollectionPanelOptions = () => ({
    tabs: [
      CollectionsPanelTabViewOptions.VOD_SEARCH,
      CollectionsPanelTabViewOptions.LIVE_SEARCH,
      CollectionsPanelTabViewOptions.CHANNEL_SEARCH,
      CollectionsPanelTabViewOptions.BROWSE_COLLECTONS
    ]
  });

  // Show skeleton while validating collection type
  if (isValidating) {
    return <CollectionSkeleton />;
  }

  return (
    <>
      <div data-testid={collectionsTestIds.root} className={classes.root}>
        <Allotment minSize={580} defaultSizes={sizes} onChange={onSplitPaneChange}>
          <Allotment.Pane>
            <div className={classes.wrapper}>
              {!collectionId ? (
                <CollectionsBrowser
                  selectedCollection={selectedCollection}
                  setSelectedCollection={setSelectedCollection}
                />
              ) : (
                <CollectionManager collection={selectedCollection} onGoBack={handleGoBack} useSelected />
              )}
            </div>
          </Allotment.Pane>
          <Allotment.Pane visible={!expanded}>
            <CollectionsPanel forKids={selectedCollection?.kids} options={getCollectionPanelOptions()} />
          </Allotment.Pane>
        </Allotment>
      </div>
      <CollectionForm />
      <CollectionLayoutsModal />
    </>
  );
}

export default Collections;
